import React from 'react'
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  OutlinedInput,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import { IReduxState } from 'interfaces/IReduxState'
import { useSelector } from 'react-redux'
import { DatePicker } from '@material-ui/pickers'
import { IPurchaseOrder, IPurchaseOrderItem } from 'interfaces/IPurchaseOrder'
import ClearIcon from '@mui/icons-material/Clear'
import AddIcon from '@mui/icons-material/Add'
import LoadIngredientAutocomplete from 'shared/autocomplete/loadIngredientAutocomplete'
import LoadPackagingPartAutocomplete from 'shared/autocomplete/loadPackagingPartAutocomplete'

export const TYPE_INGREDIENT = 'INGREDIENT'
export const TYPE_PACKAGING = 'PACKAGING'

export default function PurchaseOrderForm(props: {
  purchaseOrderData: IPurchaseOrder
  handlePurchaseOrderFieldChange: (value: any, key: string) => void
  purchaseOrderItems: IPurchaseOrderItem[]
  handlePurchaseOrderItemFieldChange: (
    value: any,
    key: string,
    changedIdx: number
  ) => void
  handleDeletePurchaseOrderItem: (idxToDelete: number) => void
  handleAddPurchaseOrderItem: () => void
}) {
  const {
    handlePurchaseOrderFieldChange,
    purchaseOrderData,
    purchaseOrderItems,
    handlePurchaseOrderItemFieldChange,
    handleDeletePurchaseOrderItem,
    handleAddPurchaseOrderItem,
  } = props
  const currentUser = useSelector(
    (state: IReduxState) => state.currentUser || {}
  )

  return (
    <Paper>
      <List>
        <ListItem>
          <TextField
            fullWidth
            label="No. PO"
            value={purchaseOrderData.poNumber}
            placeholder="25/PO-LWS/XXX"
            onChange={(e) => {
              handlePurchaseOrderFieldChange(e.target.value, 'poNumber')
            }}
          />
        </ListItem>
        <ListItem>
          <DatePicker
            required
            margin="normal"
            format="dd/MM/yyyy"
            label="Tanggal PO"
            fullWidth
            value={purchaseOrderData.poDate}
            onChange={(e) => handlePurchaseOrderFieldChange(e, 'poDate')}
            placeholder={`${new Date().getUTCDate()}/${new Date().getUTCMonth()}/${new Date().getUTCFullYear()}`}
          />
        </ListItem>
        <ListItem>
          <TextField
            fullWidth
            label="Supplier"
            value={purchaseOrderData.supplier}
            placeholder="Nama Supplier"
            onChange={(e) => {
              handlePurchaseOrderFieldChange(e.target.value, 'supplier')
            }}
          />
        </ListItem>
        <ListItem>
          <FormControl component="fieldset">
            <FormLabel>Jenis PO</FormLabel>
            <RadioGroup
              row
              aria-label="position"
              value={purchaseOrderData.type}
              onChange={(e) =>
                handlePurchaseOrderFieldChange(e.target.value, 'type')
              }
            >
              <FormControlLabel
                value={TYPE_INGREDIENT}
                control={<Radio color="primary" />}
                label="Bahan Baku"
              />
              <FormControlLabel
                value={TYPE_PACKAGING}
                control={<Radio color="primary" />}
                label="Kemasan"
              />
            </RadioGroup>
          </FormControl>
        </ListItem>
      </List>
      <Box p={2} mt={3}>
        <Box p={1}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h6">Tabel PO Item</Typography>
            </Grid>
          </Grid>
        </Box>
        {purchaseOrderItems.map((purchaseOrderItem, idx) => (
          <Box key={idx} mt={1} mb={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <Typography>Item #{idx + 1}</Typography>
              </Grid>
              <Grid item xs={12} md={9}>
                {purchaseOrderData.type === TYPE_INGREDIENT && (
                  <LoadIngredientAutocomplete
                    name={purchaseOrderItem.itemId}
                    onChange={(e) => {
                      handlePurchaseOrderItemFieldChange(
                        e.ingredientId,
                        'itemId',
                        idx
                      )
                      handlePurchaseOrderItemFieldChange(
                        e.ingredientName,
                        'itemName',
                        idx
                      )
                    }}
                  />
                )}
                {purchaseOrderData.type === TYPE_PACKAGING && (
                  <LoadPackagingPartAutocomplete
                    name={purchaseOrderItem.itemId}
                    onChange={(e) => {
                      handlePurchaseOrderItemFieldChange(
                        e.packagingPartId,
                        'itemId',
                        idx
                      )
                      handlePurchaseOrderItemFieldChange(
                        e.partName,
                        'itemName',
                        idx
                      )
                    }}
                  />
                )}
                {purchaseOrderData.type !== TYPE_PACKAGING &&
                  purchaseOrderData.type !== TYPE_INGREDIENT && (
                    <Typography>Pilih jenis PO terlebih dahulu.</Typography>
                  )}
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  autoComplete="off"
                  variant="outlined"
                  label="Harga Satuan (Rp)"
                  type="number"
                  value={purchaseOrderItem.unitPrice ?? ''}
                  fullWidth
                  onChange={(e) =>
                    handlePurchaseOrderItemFieldChange(
                      e.target.value,
                      'unitPrice',
                      idx
                    )
                  }
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  variant="outlined"
                  name="packagingPartId"
                  label="Diskon (Rp)"
                  type="number"
                  value={purchaseOrderItem.discount ?? ''}
                  onChange={(e) =>
                    handlePurchaseOrderItemFieldChange(
                      e.target.value,
                      'discount',
                      idx
                    )
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControl variant="outlined">
                  <InputLabel htmlFor="quantityPurchase">Quantity</InputLabel>
                  <OutlinedInput
                    value={purchaseOrderItem.quantityPurchased ?? ''}
                    type="number"
                    onChange={(e) =>
                      handlePurchaseOrderItemFieldChange(
                        e.target.value,
                        'quantityPurchased',
                        idx
                      )
                    }
                    id="quantityPurchase"
                    name="packagingPartId"
                    label="Qty PO"
                    placeholder="Input nomor tanpa unit"
                    fullWidth
                    endAdornment={
                      <InputAdornment position="end">
                        {purchaseOrderData.type === TYPE_INGREDIENT && (
                          <Typography>KG</Typography>
                        )}
                        {purchaseOrderData.type === TYPE_PACKAGING && (
                          <Typography>PCS</Typography>
                        )}
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </Grid>

              <Grid item xs={6} md={3}>
                <FormControl component="fieldset">
                  <FormLabel>Jenis PPN</FormLabel>
                  <RadioGroup
                    row
                    aria-label="position"
                    value={purchaseOrderItem.taxType}
                    onChange={(e) =>
                      handlePurchaseOrderItemFieldChange(
                        e.target.value,
                        'taxType',
                        idx
                      )
                    }
                  >
                    <FormControlLabel
                      value={'PPN'}
                      control={<Radio color="primary" />}
                      label="PPN"
                    />
                    <FormControlLabel
                      value={'NON_PPN'}
                      control={<Radio color="primary" />}
                      label="NON PPN"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  variant="outlined"
                  name="notes"
                  label="Notes Tentang Item"
                  placeholder="cth. datang sudah dipasang"
                  value={purchaseOrderItem.notes}
                  onChange={(e) =>
                    handlePurchaseOrderItemFieldChange(
                      e.target.value,
                      'notes',
                      idx
                    )
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography>
                  Total Harga:{ }
                  {new Intl.NumberFormat(undefined, {
                    style: 'currency',
                    currency: 'IDR',
                    minimumFractionDigits: 0,
                  }).format(
                    (purchaseOrderItem.quantityPurchased ?? 0) *
                    (purchaseOrderItem.unitPrice ?? 0) -
                    (purchaseOrderItem.discount ?? 0)
                  )}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => handleDeletePurchaseOrderItem(idx)}
                  startIcon={<ClearIcon />}
                >
                  Hapus Item
                </Button>
              </Grid>
            </Grid>
            <Box mt={3} mb={3}>
              <Divider />
            </Box>
          </Box>
        ))}
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddPurchaseOrderItem}
          startIcon={<AddIcon />}
        >
          Tambah Item
        </Button>
      </Box>
      <ListItem>
        <Typography variant="body2">
          PO dibuat oleh: {currentUser.username || '-'}
        </Typography>
      </ListItem>
      <ListItem>
        <TextField
          fullWidth
          label="Notes Buat PO"
          value={purchaseOrderData.notes}
          placeholder="cth. partial shipment"
          onChange={(e) => {
            handlePurchaseOrderFieldChange(e.target.value, 'notes')
          }}
        />
      </ListItem>
      <ListItem>
        <TextField
          fullWidth
          label="Notes Penutup"
          value={purchaseOrderData.closedNotes || ''}
          disabled
        />
      </ListItem>
      <ListItem>
        <TextField
          fullWidth
          label="Tanggal Ditutup"
          value={
            purchaseOrderData.closedDate
              ? new Date(purchaseOrderData.closedDate).toLocaleDateString(
                'id-ID'
              )
              : ''
          }
          disabled
        />
      </ListItem>
    </Paper>
  );
}
